import {omneticApi} from '@dms/api/base';

import {
  AddRentalPriceGroupItemApiResponse,
  AddRentalPriceGroupItemApiArg,
  CreateRentalPriceGroupApiResponse,
  CreateRentalPriceGroupApiArg,
  ListRentalPriceGroupApiResponse,
  ListRentalPriceGroupApiArg,
  GetRentalPriceGroupApiResponse,
  GetRentalPriceGroupApiArg,
  RemoveRentalPriceGroupApiResponse,
  RemoveRentalPriceGroupApiArg,
  RemoveRentalPriceGroupItemApiResponse,
  RemoveRentalPriceGroupItemApiArg,
  UpdateRentalPriceGroupItemApiResponse,
  UpdateRentalPriceGroupItemApiArg,
  UpdateRentalPriceGroupApiResponse,
  UpdateRentalPriceGroupApiArg,
  ActivateRentalVehicleApiResponse,
  ActivateRentalVehicleApiArg,
  DeactivateRentalVehicleApiResponse,
  DeactivateRentalVehicleApiArg,
  GetRentalVehicleApiResponse,
  GetRentalVehicleApiArg,
  PutRentalVehicleApiResponse,
  PutRentalVehicleApiArg,
  ChangeRentalVehicleBranchApiResponse,
  ChangeRentalVehicleBranchApiArg,
} from './types';

const vehicleRentalApi = omneticApi.injectEndpoints({
  endpoints: (build) => ({
    addRentalPriceGroupItem: build.mutation<
      AddRentalPriceGroupItemApiResponse,
      AddRentalPriceGroupItemApiArg
    >({
      query: (queryArg) => ({
        url: `/dms/v1/rental-price-group/${queryArg.rentalPriceGroupId}/item`,
        method: 'POST',
        body: queryArg.rentalPriceGroupItemRequestBody,
      }),
      invalidatesTags: (result, error, queryArg) => [
        {type: 'RentalPriceGroup', id: queryArg.rentalPriceGroupId},
      ],
    }),
    changeRentalVehicleBranch: build.mutation<
      ChangeRentalVehicleBranchApiResponse,
      ChangeRentalVehicleBranchApiArg
    >({
      query: (queryArg) => ({
        url: `/dms/v1/rental-vehicle/${queryArg.vehicleId}/branch`,
        method: 'PUT',
        body: queryArg.changeRentalVehicleBranchRequestBody,
      }),
      invalidatesTags: (result, error, queryArg) => [
        {type: 'RentalVehicle', id: queryArg.vehicleId},
      ],
    }),
    createRentalPriceGroup: build.mutation<
      CreateRentalPriceGroupApiResponse,
      CreateRentalPriceGroupApiArg
    >({
      query: (queryArg) => ({
        url: `/dms/v1/rental-price-group`,
        method: 'POST',
        body: queryArg.createRentalPriceGroupRequestBody,
      }),
    }),
    listRentalPriceGroup: build.query<ListRentalPriceGroupApiResponse, ListRentalPriceGroupApiArg>({
      query: () => ({
        url: `/dms/v1/rental-price-group`,
      }),
    }),
    getRentalPriceGroup: build.query<GetRentalPriceGroupApiResponse, GetRentalPriceGroupApiArg>({
      query: (queryArg) => ({
        url: `/dms/v1/rental-price-group/${queryArg.rentalPriceGroupId}`,
      }),
      providesTags: (result, error, queryArg) => [
        {type: 'RentalPriceGroup', id: queryArg.rentalPriceGroupId},
      ],
    }),
    removeRentalPriceGroup: build.mutation<
      RemoveRentalPriceGroupApiResponse,
      RemoveRentalPriceGroupApiArg
    >({
      query: (queryArg) => ({
        url: `/dms/v1/rental-price-group/${queryArg.rentalPriceGroupId}`,
        method: 'DELETE',
      }),
    }),
    removeRentalPriceGroupItem: build.mutation<
      RemoveRentalPriceGroupItemApiResponse,
      RemoveRentalPriceGroupItemApiArg
    >({
      query: (queryArg) => ({
        url: `/dms/v1/rental-price-group/${queryArg.rentalPriceGroupId}/item/${queryArg.rentalPriceGroupItemId}`,
        method: 'DELETE',
      }),
      invalidatesTags: (result, error, queryArg) => [
        {type: 'RentalPriceGroup', id: queryArg.rentalPriceGroupId},
      ],
    }),
    updateRentalPriceGroupItem: build.mutation<
      UpdateRentalPriceGroupItemApiResponse,
      UpdateRentalPriceGroupItemApiArg
    >({
      query: (queryArg) => ({
        url: `/dms/v1/rental-price-group/${queryArg.rentalPriceGroupId}/item/${queryArg.rentalPriceGroupItemId}`,
        method: 'PUT',
        body: queryArg.rentalPriceGroupItemRequestBody,
      }),
      invalidatesTags: (result, error, queryArg) => [
        {type: 'RentalPriceGroup', id: queryArg.rentalPriceGroupId},
      ],
    }),
    updateRentalPriceGroup: build.mutation<
      UpdateRentalPriceGroupApiResponse,
      UpdateRentalPriceGroupApiArg
    >({
      query: (queryArg) => ({
        url: `/dms/v1/rental-price-group/${queryArg.rentalPriceGroupId}`,
        method: 'PUT',
        body: queryArg.updateRentalPriceGroupRequestBody,
      }),
      invalidatesTags: (result, error, queryArg) => [
        {type: 'RentalPriceGroup', id: queryArg.rentalPriceGroupId},
      ],
    }),
    activateRentalVehicle: build.mutation<
      ActivateRentalVehicleApiResponse,
      ActivateRentalVehicleApiArg
    >({
      query: (queryArg) => ({
        url: `/dms/v1/rental-vehicle/${queryArg.vehicleId}/activate`,
        method: 'POST',
      }),
      invalidatesTags: (result, error, queryArg) => [
        {type: 'RentalVehicle', id: queryArg.vehicleId},
        {type: 'Vehicle', id: queryArg.vehicleId},
      ],
    }),
    deactivateRentalVehicle: build.mutation<
      DeactivateRentalVehicleApiResponse,
      DeactivateRentalVehicleApiArg
    >({
      query: (queryArg) => ({
        url: `/dms/v1/rental-vehicle/${queryArg.vehicleId}/deactivate`,
        method: 'POST',
      }),
      invalidatesTags: (result, error, queryArg) => [
        {type: 'RentalVehicle', id: queryArg.vehicleId},
        {type: 'Vehicle', id: queryArg.vehicleId},
      ],
    }),
    getRentalVehicle: build.query<GetRentalVehicleApiResponse, GetRentalVehicleApiArg>({
      query: (queryArg) => ({
        url: `/dms/v1/rental-vehicle/${queryArg.vehicleId}`,
      }),
      providesTags: (result, error, queryArg) => [{type: 'RentalVehicle', id: queryArg.vehicleId}],
    }),
    putRentalVehicle: build.mutation<PutRentalVehicleApiResponse, PutRentalVehicleApiArg>({
      query: (queryArg) => ({
        url: `/dms/v1/rental-vehicle/${queryArg.vehicleId}`,
        method: 'PUT',
        body: queryArg.rentalVehicleRequestBody,
      }),
      invalidatesTags: (result, error, queryArg) => [
        {type: 'RentalVehicle', id: queryArg.vehicleId},
      ],
    }),
  }),
  overrideExisting: false,
});

export const {
  useChangeRentalVehicleBranchMutation,
  useAddRentalPriceGroupItemMutation,
  useCreateRentalPriceGroupMutation,
  useListRentalPriceGroupQuery,
  useGetRentalPriceGroupQuery,
  useRemoveRentalPriceGroupMutation,
  useRemoveRentalPriceGroupItemMutation,
  useUpdateRentalPriceGroupItemMutation,
  useUpdateRentalPriceGroupMutation,
  useActivateRentalVehicleMutation,
  useDeactivateRentalVehicleMutation,
  useGetRentalVehicleQuery,
  usePutRentalVehicleMutation,
} = vehicleRentalApi;
